import type { ForwardedRef } from "react";
import { forwardRef, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { SelectedPlace } from "@/entities/place";
import { useFieldRefs, useFormAPI, useValidation } from "@/shared/forms";
import { Autocomplete } from "@/shared/ui/ancestry";
import { useFieldContext } from "../FieldContext";
import type { PlaceAutocompleteProps } from "./types";

const PLACE_AUTOCOMPLETE_SOURCE = "https://placepfx.ancestry.com/s/?&maxCount=10&cultureId=en_US";

/**
 * Place-specific search field. The generic own-the-node autocomplete mechanics
 * (owning the `<input>`, gating attach on `window.ui.autocomplete` readiness, and the
 * attach/destroy lifecycle) live in `@/shared/ui/ancestry`'s `Autocomplete`; this
 * component keeps only the place concerns: validation, form-field wiring, the place
 * data source, and normalizing typed text to a matched place on close.
 */
export const PlaceAutocompleteField = forwardRef<HTMLInputElement, PlaceAutocompleteProps>(
    function PlaceAutocompleteField(
        { ariaLabel, id, placeholder, value, onChange, required, ...rest },
        _ref: ForwardedRef<HTMLInputElement>,
    ): JSX.Element {
        const { t } = useTranslation();
        useValidation(id, {
            errorLocation: "callout",
            rules: { required },
        });

        const { fieldPath } = useFieldContext();
        const { getFieldValue } = useFormAPI();
        const { registerFieldRef, unregisterFieldRef } = useFieldRefs();
        const resultsRef = useRef<SelectedPlace[]>([]);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
            onChange({ ...value, location: e.target.value, gpid: null });
        };

        const handleItemSelect = ({ item }: { item: SelectedPlace }): void => {
            onChange({ ...value, location: item.HName, gpid: item.Id });
        };

        const handleResponse = ({ results }: { results: SelectedPlace[] }): void => {
            resultsRef.current = results;

            const match = results.find(
                (result) =>
                    result.HName.replace(/\s+/g, "").toLowerCase() ===
                    value?.location.replace(/\s+/g, "").toLowerCase(),
            );

            if (match) onChange({ ...value, gpid: match.Id });
        };

        const handleClose = ({ element }: { element: HTMLInputElement }): void => {
            const inputValue = element.value;
            // We may get stale values in "field" reference, so we retrieve it programatically
            const currentValue: PlaceAutocompleteProps["value"] = getFieldValue(fieldPath);
            // Handle race condition, as handleClose may be triggered right after handleItemSelect
            if (!inputValue || inputValue === currentValue?.location) return;

            const match = resultsRef.current.find(
                (result) =>
                    result.HName.replace(/\s+/g, "").toLowerCase() === inputValue.replace(/\s+/g, "").toLowerCase(),
            );

            if (match) {
                onChange({ ...value, location: match.HName, gpid: match.Id });
            }
        };

        const registerInput = (el: HTMLInputElement | null): void => {
            if (el) {
                registerFieldRef(`${fieldPath}.location`, el as unknown as React.RefObject<HTMLElement>);
            }
        };

        useEffect(() => unregisterFieldRef(`${fieldPath}.location`), [fieldPath, unregisterFieldRef]);

        return (
            <Autocomplete<SelectedPlace>
                ref={registerInput}
                id={id}
                aria-label={ariaLabel}
                placeholder={placeholder ?? t("event.enterLocationPlaceholder")}
                value={value?.location ?? ""}
                onChange={handleChange}
                source={PLACE_AUTOCOMPLETE_SOURCE}
                searchKey="HName"
                queryParameter="prefix"
                onItemSelect={handleItemSelect}
                onResponse={handleResponse}
                onClose={handleClose}
                {...rest}
            />
        );
    },
);
